import XLSXStyle from "xlsx-js-style";

// Generador de la plantilla de Excel para cargar insumos en Cotizaciones, Ventas,
// Pedidos y Embarques. Vive aparte del modal (sin React ni antd) para que se pueda
// abrir el .xlsx generado y revisarlo sin levantar la app.

// Formato Contabilidad de Excel: el $ pegado al borde izquierdo y el numero a la
// derecha. Es el estandar de todos los Excel que genera la app.
export const FORMATO_CONTABILIDAD = '_-"$"* #,##0.00_-;-"$"* #,##0.00_-;_-"$"* "-"??_-;_-@_-';

const COLOR_MARCA = "00BFD6";

export const COLUMNAS_PLANTILLA = [
  { titulo: "Clave", ancho: 16 },
  { titulo: "Descripcion", ancho: 40 },
  { titulo: "Cantidad", ancho: 12 },
  { titulo: "Precio Unitario", ancho: 18 },
  { titulo: "Descuento (%)", ancho: 14 },
  { titulo: "Leyenda", ancho: 35 },
  { titulo: "Observaciones", ancho: 35 },
];

const RENGLONES_EN_BLANCO = 12;

const bordeFino = { style: "thin", color: { rgb: "BFBFBF" } };
const bordes = { top: bordeFino, bottom: bordeFino, left: bordeFino, right: bordeFino };

const estiloTitulo = {
  font: { bold: true, sz: 14, color: { rgb: "FFFFFF" } },
  fill: { fgColor: { rgb: COLOR_MARCA } },
  alignment: { horizontal: "center", vertical: "center" },
};
const estiloEncabezado = {
  font: { bold: true, sz: 11, color: { rgb: "1A3A4A" } },
  fill: { fgColor: { rgb: "D6F4F8" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: bordes,
};
const estiloEjemplo = {
  font: { italic: true, sz: 10, color: { rgb: "8C8C8C" } },
  border: bordes,
  alignment: { vertical: "center", wrapText: true },
};
const estiloEjemploMoneda = { ...estiloEjemplo, numFmt: FORMATO_CONTABILIDAD };
const estiloVacio = { border: bordes };
const estiloVacioMoneda = { border: bordes, numFmt: FORMATO_CONTABILIDAD };

// xlsx-js-style NO infiere el tipo cuando el elemento del arreglo ya es un objeto
// {v,s}: sin `t: "n"` explicito el numero se escribe como texto y el numFmt nunca
// aplica (Excel marca "numero almacenado como texto"). Las celdas que quedan en
// blanco conservan el estilo para que, si el usuario teclea encima, ya salgan con
// el formato correcto en vez del General de Excel.
const celdaNumerica = (valor, estilo) =>
  valor || valor === 0 ? { v: valor, t: "n", s: estilo } : { v: "", s: estilo };

const filaEjemplo = ({ clave, descripcion, cantidad, precio, descuento, leyenda, observaciones }) => [
  { v: clave, t: "s", s: estiloEjemplo },
  { v: descripcion, t: "s", s: estiloEjemplo },
  celdaNumerica(cantidad, estiloEjemplo),
  celdaNumerica(precio, estiloEjemploMoneda),
  celdaNumerica(descuento, estiloEjemplo),
  { v: leyenda, t: "s", s: estiloEjemplo },
  { v: observaciones, t: "s", s: estiloEjemplo },
];

const filaVacia = () =>
  COLUMNAS_PLANTILLA.map((columna) =>
    columna.titulo === "Precio Unitario"
      ? { v: "", s: estiloVacioMoneda }
      : { v: "", s: estiloVacio }
  );

const construirHojaPlantilla = (nombreDocumento) => {
  const filas = [
    [
      { v: `PLANTILLA DE INSUMOS - ${nombreDocumento.toUpperCase()}`, t: "s", s: estiloTitulo },
      // Las celdas restantes del merge necesitan el mismo estilo o el fondo cian
      // se corta a la mitad del titulo.
      ...COLUMNAS_PLANTILLA.slice(1).map(() => ({ v: "", s: estiloTitulo })),
    ],
    COLUMNAS_PLANTILLA.map((columna) => ({ v: columna.titulo, t: "s", s: estiloEncabezado })),
    filaEjemplo({
      clave: "ABC123",
      descripcion: "Producto de ejemplo",
      cantidad: 10,
      precio: 150.5,
      descuento: 0,
      leyenda: "",
      observaciones: "",
    }),
    filaEjemplo({
      clave: "XYZ789",
      descripcion: "Otro producto",
      cantidad: 2,
      precio: 1200,
      descuento: 5,
      leyenda: "Leyenda que sustituye la descripcion",
      observaciones: "Nota que sale en el PDF",
    }),
  ];
  for (let i = 0; i < RENGLONES_EN_BLANCO; i++) filas.push(filaVacia());

  const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
  hoja["!cols"] = COLUMNAS_PLANTILLA.map((columna) => ({ wch: columna.ancho }));
  hoja["!rows"] = [{ hpt: 26 }, { hpt: 30 }];
  hoja["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMNAS_PLANTILLA.length - 1 } }];
  // Sin "!freeze"/"!panes": xlsx-js-style 1.2.0 los ignora en silencio (congelar
  // encabezados es de SheetJS Pro). Sin "!autofilter": con un solo renglon de
  // encabezado la libreria lo estira hasta el final de la hoja.
  return hoja;
};

const construirHojaInstrucciones = () => {
  const estiloTituloAyuda = { font: { bold: true, sz: 12, color: { rgb: "1A3A4A" } } };
  const estiloNombreColumna = {
    font: { bold: true, sz: 10, color: { rgb: "1A3A4A" } },
    alignment: { vertical: "top" },
  };
  const estiloAyuda = { font: { sz: 10 }, alignment: { wrapText: true, vertical: "top" } };
  const renglon = (columna, explicacion) => [
    { v: columna, t: "s", s: estiloNombreColumna },
    { v: explicacion, t: "s", s: estiloAyuda },
  ];

  const filas = [
    [
      { v: "Como llenar la plantilla", t: "s", s: estiloTituloAyuda },
      { v: "", s: estiloTituloAyuda },
    ],
    [{ v: "", t: "s" }, { v: "", t: "s" }],
    renglon("Clave", "Obligatoria. Debe existir en el catalogo de insumos. Los renglones sin clave se ignoran."),
    renglon("Descripcion", "Opcional. Solo se usa para desempatar cuando dos insumos comparten la misma clave."),
    renglon("Cantidad", "Cantidad a vender."),
    renglon("Precio Unitario", "Opcional. Si se deja vacio se usa el precio de la lista de precios seleccionada en el documento."),
    renglon("Descuento (%)", "Opcional. Solo porcentaje, no monto."),
    renglon("Leyenda", "Opcional. SUSTITUYE la descripcion del insumo en el PDF y en el CFDI, y solo funciona si el insumo tiene activada la opcion 'Leyenda en Venta' en su catalogo."),
    renglon("Observaciones", "Opcional. Se imprime en el PDF debajo de la descripcion. No viaja al CFDI."),
    [{ v: "", t: "s" }, { v: "", t: "s" }],
    [
      {
        v: "Los encabezados pueden estar en cualquier fila: el sistema busca la fila que tenga la columna Clave.",
        t: "s",
        s: estiloAyuda,
      },
      { v: "", s: estiloAyuda },
    ],
  ];

  const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
  hoja["!cols"] = [{ wch: 20 }, { wch: 95 }];
  return hoja;
};

export const construirLibroPlantillaInsumos = (nombreDocumento = "Documento") => {
  const libro = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(libro, construirHojaPlantilla(nombreDocumento), "Plantilla");
  XLSXStyle.utils.book_append_sheet(libro, construirHojaInstrucciones(), "Instrucciones");
  return libro;
};

export const nombreArchivoPlantilla = (nombreDocumento = "Documento") =>
  `Plantilla_Insumos_${nombreDocumento.replace(/\s+/g, "_")}.xlsx`;

export const descargarPlantillaInsumos = (nombreDocumento = "Documento") => {
  XLSXStyle.writeFile(
    construirLibroPlantillaInsumos(nombreDocumento),
    nombreArchivoPlantilla(nombreDocumento)
  );
};
